import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import type {
  PullRequestCheck,
  PullRequestCockpit,
  PullRequestIssue,
  PullRequestMutation,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  pullRequestIssuePrompt,
  pullRequestIssues,
  pullRequestWatchPrompt,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleX,
  ExternalLink,
  GitMerge,
  GitPullRequest,
  LoaderCircle,
  MessageSquare,
  Minus,
  MoreHorizontal,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { errorMessage } from '@/lib/error-message';
import { mutatePullRequest, readPullRequestCockpit } from './pull-request-client';
import {
  CommentsDialog,
  EditDialog,
  MergeDialog,
  type Mutation,
  openExternal,
  ReviewDialog,
  ReviewersDialog,
} from './pull-request-dialogs';
import {
  type ChecksOverview,
  type CheckTone,
  checkNote,
  checksOverview,
  checkTone,
  fixAction,
  type PrimaryAction,
  primaryAction,
  type ReviewOverview,
  reviewOverview,
  stateLabel,
} from './pull-request-panel-model';

type OpenDialog = 'comments' | 'edit' | 'merge' | 'merge-when-ready' | 'review' | 'reviewers';

const STATE_BADGE = {
  open: 'success',
  draft: 'outline',
  merged: 'default',
  closed: 'destructive',
} as const;

function CheckIcon({ tone }: { tone: CheckTone }) {
  if (tone === 'passed') return <Check aria-hidden className="size-3.5 text-success" />;
  if (tone === 'failed') return <X aria-hidden className="size-3.5 text-destructive" />;
  if (tone === 'running')
    return <LoaderCircle aria-hidden className="size-3.5 animate-spin text-running" />;
  return <Minus aria-hidden className="size-3.5 text-muted-foreground" />;
}

function ChecksIcon({ tone }: { tone: ChecksOverview['tone'] }) {
  if (tone === 'failed') return <CircleX aria-hidden className="size-4 text-destructive" />;
  if (tone === 'running')
    return <LoaderCircle aria-hidden className="size-4 animate-spin text-running" />;
  if (tone === 'passed') return <CircleCheck aria-hidden className="size-4 text-success" />;
  return <Minus aria-hidden className="size-4 text-muted-foreground" />;
}

/** One check: its state, its name and its duration; opens its logs when the forge links them. */
function CheckRow({ check }: { check: PullRequestCheck }) {
  const tone = checkTone(check);
  const content = (
    <>
      <CheckIcon tone={tone} />
      <span className="min-w-0 flex-1 truncate text-left">{check.name}</span>
      <span
        className={`type-meta shrink-0 tabular-nums ${tone === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}
      >
        {checkNote(check)}
      </span>
    </>
  );
  const className = 'flex min-h-8 w-full items-center gap-3 rounded-lg px-1 text-sm';
  return (
    <li data-check-tone={tone}>
      {check.detailsUrl ? (
        <button
          className={`${className} hover:bg-accent`}
          onClick={() => openExternal(check.detailsUrl ?? '')}
          title="Open the check's logs"
          type="button"
        >
          {content}
        </button>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
}

function CommentsLabel({ review }: { review: ReviewOverview }) {
  return (
    <>
      {review.comments === 1 ? '1 comment' : `${review.comments} comments`}
      {review.unresolved > 0 && (
        <>
          {' · '}
          <span className="text-attention">{review.unresolved} unresolved</span>
        </>
      )}
    </>
  );
}

/**
 * The session's pull request in the side panel (D36, mobile 33): its title, branches and size,
 * the checks, the review and the description, with one filled action (Merge, or Merge when
 * ready) beside "Open on GitHub". Everything else the forge allows sits in the ⋯ menu.
 */
export function PullRequestPanel({
  workspace,
  taskId,
  cloud,
  visible,
  previewCockpit,
  onSendMessage,
  onAttach,
  onOpenReview,
  onRefresh,
}: {
  workspace: string | null;
  taskId: string;
  cloud: boolean;
  visible: boolean;
  /** A fixed cockpit shown instead of the session's (the design preview). */
  previewCockpit?: PullRequestCockpit | null | undefined;
  /** Sends a prompt to the session's agent. */
  onSendMessage(text: string): void;
  /** Attaches an issue to the composer. */
  onAttach?: ((issue: PullRequestIssue) => void) | undefined;
  /** Opens the review of the changed files (and their comment threads). */
  onOpenReview(): void;
  /** Re-reads the session's git status once its pull request changed. */
  onRefresh?: (() => Promise<void>) | undefined;
}) {
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const [cockpit, setCockpit] = useState<PullRequestCockpit | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [checksOpen, setChecksOpen] = useState(false);
  const preview = previewCockpit !== undefined;

  const load = useCallback(async () => {
    if (preview || !workspace || !visible) return;
    setLoading(true);
    setError(null);
    try {
      setCockpit(await readPullRequestCockpit({ workspace, taskId, cloud }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [cloud, preview, taskId, visible, workspace]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = preview ? previewCockpit : cockpit;

  const mutate = useCallback(
    async (mutation: Mutation) => {
      if (!shown || !workspace || preview) return;
      setWorking(true);
      setError(null);
      try {
        await mutatePullRequest({ workspace, taskId, cloud }, {
          ...mutation,
          expectedHeadSha: shown.headSha,
        } as PullRequestMutation);
        setDialog(null);
        await onRefresh?.().catch(() => undefined);
        await load();
      } catch (caught) {
        setError(errorMessage(caught));
      } finally {
        setWorking(false);
      }
    },
    [cloud, load, onRefresh, preview, shown, taskId, workspace],
  );

  const checks = useMemo(() => (shown ? checksOverview(shown) : null), [shown]);
  const review = useMemo(() => (shown ? reviewOverview(shown) : null), [shown]);
  const issues = useMemo(() => (shown ? pullRequestIssues(shown) : []), [shown]);

  const empty = 'p-6 text-center text-muted-foreground text-sm';
  if (!workspace && !preview)
    return <p className={empty}>Open a workspace to inspect its pull request.</p>;
  if (!shown && loading) return <p className={empty}>Loading pull request…</p>;
  if (!shown || !checks || !review)
    return (
      <div className={`${empty} grid justify-items-center gap-3`}>
        <p>{error ?? 'No pull request is attached to this task.'}</p>
        <Button onClick={() => void load()} size="sm" variant="outline">
          Refresh
        </Button>
      </div>
    );

  const pr = shown.pullRequest;
  const primary = primaryAction(shown);
  const fix = fixAction(shown);
  const close = () => !working && setDialog(null);
  const live = pr.state === 'open' || pr.state === 'draft';

  const runPrimary = (action: PrimaryAction) => {
    if (action.kind === 'ready') void mutate({ action: 'ready' });
    else if (action.kind === 'merge') setDialog('merge');
    else if (action.kind === 'merge-when-ready') setDialog('merge-when-ready');
  };

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="More pull request actions"
          className="ml-auto"
          size="icon-sm"
          variant="ghost"
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuItem disabled={loading || preview} onSelect={() => void load()}>
          Refresh
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onOpenReview}>Open in Review</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setDialog('comments')}>All comments…</DropdownMenuItem>
        {phone && fix && (
          <DropdownMenuItem
            onSelect={() => onSendMessage(pullRequestIssuePrompt(shown, fix.issues))}
          >
            {fix.label}
          </DropdownMenuItem>
        )}
        {checks.attention.some((check) => checkTone(check) === 'running') && (
          <DropdownMenuItem onSelect={() => onSendMessage(pullRequestWatchPrompt(shown))}>
            Watch the checks
          </DropdownMenuItem>
        )}
        {onAttach && issues.length > 0 && (
          <DropdownMenuItem
            onSelect={() => {
              for (const issue of issues) onAttach(issue);
            }}
          >
            Attach what fails to the message
          </DropdownMenuItem>
        )}
        {live && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setDialog('edit')}>
              Edit title and description…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog('review')}>
              Submit a review…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog('reviewers')}>
              Request reviewers…
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={working}
              onSelect={() => void mutate({ action: 'update_branch' })}
            >
              Update branch
            </DropdownMenuItem>
          </>
        )}
        {pr.state === 'open' && (
          <>
            {primary?.kind === 'merge' && (
              <DropdownMenuItem onSelect={() => setDialog('merge-when-ready')}>
                Merge when ready…
              </DropdownMenuItem>
            )}
            {shown.autoMergeEnabled && (
              <DropdownMenuItem
                disabled={working}
                onSelect={() => void mutate({ action: 'disable_auto_merge' })}
              >
                Cancel auto-merge
              </DropdownMenuItem>
            )}
            <DropdownMenuItem disabled={working} onSelect={() => void mutate({ action: 'draft' })}>
              Convert to draft
            </DropdownMenuItem>
          </>
        )}
        {(live || pr.state === 'closed') && <DropdownMenuSeparator />}
        {live && (
          <DropdownMenuItem
            disabled={working}
            onSelect={() => void mutate({ action: 'close' })}
            variant="destructive"
          >
            Close pull request
          </DropdownMenuItem>
        )}
        {pr.state === 'closed' && (
          <DropdownMenuItem disabled={working} onSelect={() => void mutate({ action: 'reopen' })}>
            Reopen
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const size = (
    <>
      {shown.changedFiles === 1 ? '1 file' : `${shown.changedFiles} files`}
      {' · '}
      <span className="text-success">+{shown.additions}</span>{' '}
      <span className="text-destructive">−{shown.deletions}</span>
    </>
  );

  const badge = (
    <Badge className="gap-1.5" data-pr-state={pr.state} variant={STATE_BADGE[pr.state]}>
      {pr.state === 'merged' ? <GitMerge /> : <GitPullRequest />}
      {stateLabel(pr.state)}
    </Badge>
  );

  const header = phone ? (
    <header className="grid gap-2">
      <div className="flex items-center gap-2">
        {badge}
        {menu}
      </div>
      <h1 className="type-page-title text-balance leading-snug">
        {pr.title} <span className="font-normal text-muted-foreground">#{pr.number}</span>
      </h1>
      <p className="type-meta font-mono text-muted-foreground">
        {pr.headBranch} → {pr.baseBranch} · {size}
      </p>
    </header>
  ) : (
    <header className="grid gap-1">
      <div className="flex min-w-0 items-center gap-2">
        {badge}
        <span className="type-meta min-w-0 truncate font-mono text-muted-foreground">
          #{pr.number} · {pr.headBranch} → {pr.baseBranch}
        </span>
        {menu}
      </div>
      <h1 className="type-card-title leading-snug">{pr.title}</h1>
      <p className="type-meta text-muted-foreground">{size}</p>
    </header>
  );

  const checksList = (
    <ul aria-label="Checks" className="grid gap-0.5">
      {shown.checks.map((check) => (
        <CheckRow check={check} key={check.id} />
      ))}
    </ul>
  );

  const rowClass =
    'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/60';

  const body = phone ? (
    <>
      <section
        aria-label="Status"
        className="divide-y divide-hairline overflow-hidden rounded-2xl border bg-card"
      >
        <div>
          <button
            aria-expanded={checksOpen}
            className={rowClass}
            disabled={shown.checks.length === 0}
            onClick={() => setChecksOpen((open) => !open)}
            type="button"
          >
            <ChecksIcon tone={checks.tone} />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-sm">{checks.title}</span>
              {checks.attention.slice(0, 2).map((check) => (
                <span className="type-meta block truncate text-muted-foreground" key={check.id}>
                  {check.name} {checkNote(check)}
                </span>
              ))}
            </span>
            {shown.checks.length > 0 && (
              <ChevronDown
                aria-hidden
                className={`size-4 text-muted-foreground transition-transform ${checksOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>
          {checksOpen && <div className="px-3 pb-3">{checksList}</div>}
        </div>
        <button className={rowClass} onClick={onOpenReview} type="button">
          <UserRound aria-hidden className="size-4 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-sm">{review.title}</span>
            <span className="type-meta block text-muted-foreground">{review.detail}</span>
          </span>
          <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
        </button>
        <button
          aria-label="Open the comments in Review"
          className={rowClass}
          onClick={onOpenReview}
          type="button"
        >
          <MessageSquare aria-hidden className="size-4 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-sm">
              {review.comments === 1 ? '1 comment' : `${review.comments} comments`}
            </span>
            <span
              className={`type-meta block ${review.unresolved > 0 ? 'text-attention' : 'text-muted-foreground'}`}
            >
              {review.unresolved > 0 ? `${review.unresolved} unresolved` : 'All resolved'}
            </span>
          </span>
          <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
        </button>
      </section>
      <section aria-label="Description" className="grid gap-2 rounded-2xl border bg-card p-4">
        <h2 className="type-meta text-muted-foreground">Description</h2>
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
          {pr.body || 'No description provided.'}
        </p>
      </section>
    </>
  ) : (
    <>
      <section aria-label="Checks summary" className="grid gap-2 rounded-2xl border bg-card p-3">
        <h2 className="type-meta px-1 text-muted-foreground">{checks.title}</h2>
        {shown.checks.length > 0 && checksList}
      </section>
      <section aria-label="Review" className="grid gap-1.5 rounded-2xl border bg-card p-3 text-sm">
        <h2 className="type-meta px-1 text-muted-foreground">{review.title}</h2>
        <p className="flex items-center gap-2 px-1">
          <Users aria-hidden className="size-3.5 text-muted-foreground" />
          {review.detail}
        </p>
        <button
          aria-label="Open the comments in Review"
          className="flex items-center gap-2 rounded-lg px-1 text-left hover:bg-accent"
          onClick={onOpenReview}
          type="button"
        >
          <MessageSquare aria-hidden className="size-3.5 text-muted-foreground" />
          <span>
            <CommentsLabel review={review} />
          </span>
        </button>
      </section>
      <section aria-label="Description" className="grid gap-1.5 px-1 text-sm">
        <h2 className="type-meta text-muted-foreground">Description</h2>
        <p className="whitespace-pre-wrap break-words leading-relaxed">
          {pr.body || 'No description provided.'}
        </p>
      </section>
    </>
  );

  const primaryButton = primary && (
    <Button
      className={phone ? 'w-full' : undefined}
      data-pr-primary={primary.kind}
      disabled={
        working ||
        primary.kind === 'auto-merge-on' ||
        (primary.kind === 'merge-when-ready' && primary.disabledReason !== null)
      }
      onClick={() => runPrimary(primary)}
      size={phone ? 'lg' : 'sm'}
      title={
        primary.kind === 'merge-when-ready'
          ? (primary.disabledReason ?? 'Merges once the checks pass and the review is approved')
          : primary.kind === 'auto-merge-on'
            ? 'Auto-merge is on; cancel it from the ⋯ menu'
            : undefined
      }
    >
      {primary.label}
    </Button>
  );

  const github = (
    <Button
      className={phone ? 'w-full' : undefined}
      onClick={() => openExternal(pr.url)}
      size={phone ? 'lg' : 'sm'}
      variant="outline"
    >
      Open on GitHub
      {!phone && <ExternalLink />}
    </Button>
  );

  return (
    <div
      className="desktop-panel-view-enter flex h-full min-h-0 flex-col"
      data-pr-layout={phone ? 'phone' : 'desktop'}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-3 pb-4 [&>*]:shrink-0">
        {header}
        {error && (
          <p
            className="rounded-xl bg-destructive/10 px-3 py-2 text-destructive text-xs"
            role="alert"
          >
            {error}
          </p>
        )}
        {body}
      </div>
      <footer
        className={
          phone
            ? `grid shrink-0 gap-2 border-hairline border-t px-4 py-3 ${primary ? 'grid-cols-2' : 'grid-cols-1'}`
            : 'flex shrink-0 flex-wrap items-center justify-end gap-2 px-4 py-3'
        }
      >
        {github}
        {!phone && fix && (
          <Button
            onClick={() => onSendMessage(pullRequestIssuePrompt(shown, fix.issues))}
            size="sm"
            variant="outline"
          >
            {fix.label}
          </Button>
        )}
        {primaryButton}
      </footer>

      {dialog === 'comments' && (
        <CommentsDialog
          close={close}
          cockpit={shown}
          mutate={mutate}
          prompt={onSendMessage}
          working={working}
        />
      )}
      {dialog === 'edit' && (
        <EditDialog close={close} cockpit={shown} mutate={mutate} working={working} />
      )}
      {dialog === 'review' && <ReviewDialog close={close} mutate={mutate} working={working} />}
      {dialog === 'reviewers' && (
        <ReviewersDialog close={close} mutate={mutate} working={working} />
      )}
      {(dialog === 'merge' || dialog === 'merge-when-ready') && (
        <MergeDialog
          close={close}
          mutate={mutate}
          whenReady={dialog === 'merge-when-ready'}
          working={working}
        />
      )}
    </div>
  );
}
